import { type Maybe } from '@amazon/vinyl-util'
import {
    clamp,
    ensureContrast,
    type Hsl,
    hslToRgb,
    type Oklch,
    oklchToRgb,
    parseColor,
    type Rgb,
    rgbaToCssString,
    rgbToCss,
    rgbToHex,
    rgbToHsl,
    rgbToOklch,
} from './color'

// ── OKLCH triadic deriver ────────────────────────────────────────────────────
//
// From a single seed color: derive a per-theme background, a primary accent at
// the seed hue, and a secondary accent shifted +40° (visually distinct while
// staying same-family). Everything is perceptual (OKLCH) with a WCAG contrast
// pass so accent/secondary stay legible on the surface they sit on.

const CHROMA_MIN = 0.12
const CHROMA_MAX = 0.2
const ACCENT_L: Record<ThemeMode, number> = { dark: 0.78, light: 0.58 }
const BG_L: Record<ThemeMode, number> = { dark: 0.3, light: 0.85 }
const SECONDARY_HUE_OFFSET = 40
const CONTRAST_ACCENT = 4.5
const CONTRAST_SECONDARY = 3

export type ThemeMode = 'light' | 'dark'

export interface Theme {
    readonly bg: string
    readonly accent: string
    readonly secondary: string
}
export interface ThemePair {
    readonly light: Theme
    readonly dark: Theme
}

/**
 * Image shape the theme generators read: the wire's
 * `colorPalette.dominantColors[].color` list, plus the single-color
 * `popColor` / `leadColor` slots used when the palette carries no dominant
 * list. Callers pass whatever subset of their image type carries the palette;
 * the index signature lets a full image object flow through unchanged since
 * only the palette is read.
 */
export type PaletteImageFields = {
    readonly colorPalette?: {
        readonly dominantColors?: readonly Maybe<{ readonly color?: string | null }>[] | null
        readonly popColor?: string | null
        readonly leadColor?: string | null
    } | null
    readonly [key: string]: unknown
}

export function chromaFor(seed: Oklch): number {
    return clamp(seed.c * 1.5, CHROMA_MIN, CHROMA_MAX)
}

// Primary + secondary accent hexes for a seed hue/chroma at lightness `l`,
// each nudged until it clears its WCAG target against `surface`.
export function accentPair(
    hue: number,
    chroma: number,
    l: number,
    surface: Rgb
): { accent: string; secondary: string } {
    const mk = (h: number, target: number) =>
        rgbToHex(oklchToRgb(ensureContrast({ l, c: chroma, h: (h + 360) % 360 }, surface, target)))
    return {
        accent: mk(hue, CONTRAST_ACCENT),
        secondary: mk(hue + SECONDARY_HUE_OFFSET, CONTRAST_SECONDARY),
    }
}

/**
 * Page theme from one seed color: a low/high-lightness OKLCH background per
 * theme, plus primary + secondary accents contrast-checked against that derived
 * background. Returns `null` for an unparseable seed.
 */
export function generateTheme(seedHex: string): ThemePair | null {
    const seedRgb = parseColor(seedHex)
    if (!seedRgb) return null
    const seed = rgbToOklch(seedRgb)
    const chroma = chromaFor(seed)
    const bg = (l: number, cMul: number, cMax: number): Rgb =>
        oklchToRgb({ l, c: Math.min(seed.c * cMul, cMax), h: seed.h })
    const darkBg = bg(BG_L.dark, 0.35, 0.06)
    const lightBg = bg(BG_L.light, 0.12, 0.025)
    return {
        dark: { bg: rgbToHex(darkBg), ...accentPair(seed.h, chroma, ACCENT_L.dark, darkBg) },
        light: { bg: rgbToHex(lightBg), ...accentPair(seed.h, chroma, ACCENT_L.light, lightBg) },
    }
}

// Lightness/max-saturation the card surface is pinned to per theme: the seed's
// HUE carries the artwork, but lightness is pinned (dark → low, light → pale)
// and saturation capped so it reads as a subtle tint, not a garish block.
export const SURFACE_PIN: Record<ThemeMode, { readonly l: number; readonly maxS: number }> = {
    dark: { l: 0.18, maxS: 0.5 },
    light: { l: 0.84, maxS: 0.35 },
}

export function surfaceRgb(seedRgb: Rgb, mode: ThemeMode): Rgb {
    const { h, s } = rgbToHsl(seedRgb)
    const { l, maxS } = SURFACE_PIN[mode]
    return hslToRgb({ h, s: Math.min(s, maxS), l, a: 1 })
}

export function surfaceTintRgb(seedRgb: Rgb, mode: ThemeMode, alpha: number): string {
    const tint = surfaceRgb(seedRgb, mode)
    return alpha < 1 ? rgbaToCssString({ ...tint, a: alpha }) : rgbToCss(tint)
}

/**
 * Lightness-pinned surface tint of a seed color per theme. `alpha < 1` yields an
 * `rgba()` string; opaque yields `rgb()`. Returns `undefined` for an
 * unparseable seed so callers clear their var and the stylesheet fallback fires.
 */
export function surfaceTint(seedHex: string, mode: ThemeMode, alpha = 1): string | undefined {
    const rgb = parseColor(seedHex)
    return rgb ? surfaceTintRgb(rgb, mode, alpha) : undefined
}

/**
 * Card theme from a lead color: accent + secondary are contrast-checked against
 * the CARD SURFACE (not a page background), since the card's L-pinned tint is
 * where the on-card text actually sits — brightest in light mode, so its
 * contrast target differs from the page's. Returns `null` for an unparseable seed.
 */
export function deriveCardTheme(seedHex: string): ThemePair | null {
    const seedRgb = parseColor(seedHex)
    if (!seedRgb) return null
    const seed = rgbToOklch(seedRgb)
    const chroma = chromaFor(seed)
    const mk = (mode: ThemeMode): Theme => {
        const surf = surfaceRgb(seedRgb, mode)
        return { bg: rgbToHex(surf), ...accentPair(seed.h, chroma, ACCENT_L[mode], surf) }
    }
    return { dark: mk('dark'), light: mk('light') }
}

/**
 * Theme surface pair derived from an image's dominant palette color — the
 * mid-tone surface behind recommendation tiles, the transport pill, and the
 * player chrome. Returns `null` when the image carries no parseable dominant
 * color so callers clear their CSS vars and the stylesheet fallback fires.
 */
export type SurfaceColorPair = { readonly dark: string; readonly light: string }

/**
 * Parse an image's dominant palette color to HSL, or null if absent. Prefers
 * the explicit `dominantColors[0]`, then the single-color wire `popColor` /
 * `leadColor`, so a hero whose palette carries only a pop/lead color still
 * themes from the wire at first paint instead of waiting on a canvas sample.
 */
export function dominantHslFromImage(image: Maybe<PaletteImageFields>): Hsl | null {
    const palette = image?.colorPalette
    const hex =
        palette?.dominantColors?.[0]?.color ?? palette?.popColor ?? palette?.leadColor ?? null
    if (!hex) return null
    const rgb = parseColor(hex)
    if (!rgb) return null
    return rgbToHsl(rgb)
}

export function dominantThemeColors(image: Maybe<PaletteImageFields>): SurfaceColorPair | null {
    const hsl = dominantHslFromImage(image)
    if (!hsl) return null
    const rgb = hslToRgb(hsl)
    return { dark: surfaceTintRgb(rgb, 'dark', 1), light: surfaceTintRgb(rgb, 'light', 1) }
}

export const T1R_SURFACE_MAX_S = 0.5
const T1R_FADE_LIGHT_L = 0.7
const T1R_FADE_DARK_L = 0.2
// A sampled left edge at/above this luminance reads LIGHT, flipping the hero to dark-on-light.
const T1R_LIGHT_L = 0.7
// Contrast text mirrors the shared --header-color values (light #1a1a1a, dark #fff).
const T1R_TEXT_ON_LIGHT = '#1a1a1a'
const T1R_TEXT_ON_DARK = '#ffffff'

/**
 * The t1r hero's treatment from the artwork's left-edge dominant: the fill
 * behind the hero text (`fade`), its contrasting `text`, and whether the fill
 * reads LIGHT (`isLight`, the flag that flips the hero to dark-on-light).
 */
export type T1rFadeColors = {
    readonly fade: string
    readonly text: string
    readonly isLight: boolean
}

/**
 * The t1r hero's fade colors from the artwork's left-edge dominant. The fill's
 * luminance is IMAGE-driven, not fixed by the app theme: a light edge is pushed
 * to a pale fill (dark text on it), a dark edge to a deep fill (light text) — so
 * the hero's light/dark treatment follows the artwork. Hue is kept and saturation
 * capped so a pale fill never reads neon.
 */
export function t1rFadeColorsFromHsl(hsl: Hsl): T1rFadeColors {
    const isLight = hsl.l >= T1R_LIGHT_L
    const s = Math.min(hsl.s, T1R_SURFACE_MAX_S)
    return {
        fade: rgbToCss(
            hslToRgb({
                ...hsl,
                s,
                l: isLight ? Math.max(hsl.l, T1R_FADE_LIGHT_L) : Math.min(hsl.l, T1R_FADE_DARK_L),
            })
        ),
        text: isLight ? T1R_TEXT_ON_LIGHT : T1R_TEXT_ON_DARK,
        isLight,
    }
}
